import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";

import { Button, CircularProgress, makeStyles } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import Alert from "@material-ui/lab/Alert";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import LOGO from "src/assets/svgs/logo.png";

import { supportLogin } from "src/apiService/modules/support";
import { setSupportLoginToken } from "src/store/support/actions";
import {
  getInvitationStatus,
  acceptInvitation,
  acceptInvitationWithJwt,
  type InvitationStatus,
} from "src/apiService/modules/accountant";
import PasswordField from "src/components/PasswordField";

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  form: { width: "100%", marginTop: theme.spacing(2) },
  submit: { margin: theme.spacing(3, 0, 2), padding: 12, position: "relative" },
  loading: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: "-12px",
    marginLeft: "-12px",
  },
  intro: { marginTop: theme.spacing(1), textAlign: "center" },
  hint: { marginTop: theme.spacing(1), textAlign: "center" },
  back: { marginTop: theme.spacing(2), textAlign: "center" },
}));

interface FormValues {
  email: string;
  password: string;
  confirm: string;
}

function InviteAccept() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { invitationId } = useParams<{ invitationId: string }>();

  const [status, setStatus] = useState<InvitationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getInvitationStatus(invitationId);
        if (!cancelled) setStatus(result);
      } catch (e) {
        if (!cancelled)
          setLoadError(
            "This invitation link is invalid. Please ask your client to resend it."
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invitationId]);

  const accountantExists = status?.accountantExists ?? false;

  const formik = useFormik<FormValues>({
    initialValues: { email: "", password: "", confirm: "" },
    validate: (values) => {
      const errors: Partial<FormValues> = {};
      if (accountantExists) {
        const email = values.email.trim();
        if (!email) errors.email = "Please enter your email address.";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
          errors.email = "Please enter a valid email address.";
        if (!values.password) errors.password = "Please enter your password.";
      } else {
        if (values.password.length < 8)
          errors.password = "Use at least 8 characters.";
        if (values.confirm !== values.password)
          errors.confirm = "Passwords don't match.";
      }
      return errors;
    },
    onSubmit: async (values, { setStatus: setFormStatus }) => {
      if (!status) return;
      setFormStatus(undefined);
      try {
        if (accountantExists) {
          // Flow 3 — existing accountant signs in with the full email they
          // type (status only exposes a masked address), then accepts.
          let result;
          try {
            result = await supportLogin(
              values.email.trim().toLowerCase(),
              values.password
            );
          } catch {
            setFormStatus({ error: "Invalid email / password" });
            return;
          }
          if (!result.data.token || result.data.role !== "accountant") {
            setFormStatus({ error: "Invalid email / password" });
            return;
          }
          dispatch(
            setSupportLoginToken(
              result.data.token,
              result.data.role ?? "accountant"
            )
          );
          await acceptInvitationWithJwt(result.data.token, invitationId);
        } else {
          // Flow 2 — new accountant creates a login from the invite.
          const { token } = await acceptInvitation(
            invitationId,
            values.password
          );
          dispatch(setSupportLoginToken(token, "accountant"));
        }
        history.push("/accountant");
      } catch (e) {
        setFormStatus({ error: (e as Error).message || "System error" });
      }
    },
  });

  let body: React.ReactNode;
  if (loading) {
    body = (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  } else if (loadError) {
    body = (
      <Alert severity="error" style={{ marginTop: 16, width: "100%" }}>
        {loadError}
      </Alert>
    );
  } else if (status && (status.expired || status.accepted)) {
    body = (
      <Alert severity="info" style={{ marginTop: 16, width: "100%" }}>
        {status.accepted
          ? "This invitation has already been accepted. Please sign in."
          : "This invitation has expired. Please ask your client to resend it."}
      </Alert>
    );
  } else if (status) {
    body = (
      <>
        <Typography className={classes.intro}>
          <strong>{status.inviterName}</strong> invited you to access their
          books on My Reseller Genie.
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          {accountantExists
            ? "Sign in to your accountant account to accept."
            : "Create your accountant login to accept."}
        </Typography>
        {status.accountantEmail ? (
          <Typography
            className={classes.hint}
            variant="body2"
            color="textSecondary"
          >
            This invitation was sent to{" "}
            <strong>{status.accountantEmail}</strong>
            {accountantExists
              ? ". Enter that email address in full to sign in."
              : "."}
          </Typography>
        ) : null}
        <form
          className={classes.form}
          noValidate
          onSubmit={formik.handleSubmit}
        >
          {accountantExists ? (
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="invite-email"
              name="email"
              label="Email"
              type="email"
              autoComplete="username"
              autoFocus
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              error={formik.touched.email && !!formik.errors.email}
              helperText={
                formik.touched.email ? formik.errors.email : undefined
              }
              disabled={formik.isSubmitting}
            />
          ) : null}
          <PasswordField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="invite-password"
            name="password"
            label={accountantExists ? "Password" : "Create a password"}
            autoFocus={!accountantExists}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            error={formik.touched.password && !!formik.errors.password}
            helperText={
              formik.touched.password ? formik.errors.password : undefined
            }
            disabled={formik.isSubmitting}
          />
          {!accountantExists && (
            <PasswordField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="invite-confirm"
              name="confirm"
              label="Confirm password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirm}
              error={formik.touched.confirm && !!formik.errors.confirm}
              helperText={
                formik.touched.confirm ? formik.errors.confirm : undefined
              }
              disabled={formik.isSubmitting}
            />
          )}
          {formik.status?.error && !formik.isSubmitting && (
            <Alert severity="error">{formik.status.error}</Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={formik.isSubmitting}
          >
            {accountantExists ? "Sign in & accept" : "Create account & accept"}
            {formik.isSubmitting && (
              <CircularProgress
                className={classes.loading}
                size={24}
                color="inherit"
              />
            )}
          </Button>
        </form>
      </>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Box className="mb-5 h-36">
          <img src={LOGO} alt="My Reseller Genie" className="h-full" />
        </Box>
        {body}
        <div className={classes.back}>
          <Link component={RouterLink} to="/accountant/login" variant="body2">
            Go to accountant sign in
          </Link>
        </div>
      </div>
    </Container>
  );
}

export default InviteAccept;
